// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { isFleetChatEnabled } from '../config';
import {
  appendAssistantToken,
  buildApproveBody,
  cancelFleetChatJob,
  clearFleetChatSession,
  fleetChatHttpError,
  fleetChatNetworkError,
  fleetChatPendingIsLive,
  formatFleetChatResult,
  loadFleetChatSession,
  pollFleetChatJob,
  runFleetChatJob,
  saveFleetChatSession,
  stampTurnElapsed,
  stageFleetChatFile,
  transcriptForModel,
  validateFleetChatAttachFile,
} from '../utils/fleetChat';

const FleetChatContext = createContext(null);

function wireguardHost() {
  if (typeof window === 'undefined') return '';
  return String(window.RUNTIME_CONFIG?.host || '').trim();
}

function pageHost() {
  if (typeof window === 'undefined') return '';
  return window.location.hostname;
}

function pageProtocol() {
  if (typeof window === 'undefined') return '';
  return window.location.protocol;
}

function wrapFleetChatError(err) {
  const statusMatch = /Fleet Chat HTTP (\d+)/.exec(err?.message || '');
  if (statusMatch) {
    return new Error(
      fleetChatHttpError(Number(statusMatch[1]), wireguardHost(), pageHost(), pageProtocol())
    );
  }
  return new Error(fleetChatNetworkError(err, wireguardHost(), pageHost(), pageProtocol()));
}

async function readFleetChatJob(body, onEvent, onJobId, onCursor, signal) {
  try {
    await runFleetChatJob({
      postUrl: '/api/assistant/fleet-chat',
      pollUrlFor: (jobId) => `/api/assistant/fleet-chat/jobs/${jobId}`,
      body,
      tokenFor: () => (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : ''),
      onEvent,
      onJobId,
      onCursor,
      signal,
      cancelOnAbort: false,
    });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    throw wrapFleetChatError(err);
  }
}

const DISABLED_FLEET_CHAT = {
  messages: [],
  input: '',
  setInput: () => {},
  busy: false,
  ready: true,
  error: null,
  setError: () => {},
  pending: null,
  attachment: null,
  attachProgress: null,
  attachFile: async () => {},
  clearAttachment: () => {},
  sendMessage: async () => {},
  approvePendingWithAck: async () => {},
  denyPending: () => {},
  clearTranscript: () => {},
};

export function FleetChatProvider({ children }) {
  if (!isFleetChatEnabled()) {
    return <FleetChatContext.Provider value={DISABLED_FLEET_CHAT}>{children}</FleetChatContext.Provider>;
  }
  return <FleetChatSessionProvider>{children}</FleetChatSessionProvider>;
}

function FleetChatSessionProvider({ children }) {
  const { currentUser } = useAuth();
  const username = String(currentUser?.username || '').trim();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [attachProgress, setAttachProgress] = useState(null);
  const abortRef = useRef(null);
  const busyRef = useRef(false);
  const jobIdRef = useRef(null);
  const afterRef = useRef(0);
  const startedAtRef = useRef(null);
  const messagesRef = useRef([]);
  const pendingRef = useRef(null);
  const attachmentRef = useRef(null);
  const inputRef = useRef('');
  const usernameRef = useRef(username);
  const hydratedRef = useRef(false);
  const handleEventRef = useRef(null);
  const persistNowRef = useRef(null);
  const finishTurnClockRef = useRef(null);
  const [ready, setReady] = useState(false);

  const persistNow = useCallback(() => {
    const name = usernameRef.current;
    if (!name || typeof sessionStorage === 'undefined') return;
    if (pendingRef.current && !fleetChatPendingIsLive(pendingRef.current)) {
      pendingRef.current = null;
      setPending(null);
    }
    saveFleetChatSession(sessionStorage, name, {
      messages: messagesRef.current,
      input: inputRef.current,
      pending: pendingRef.current,
      attachment: attachmentRef.current,
      jobId: jobIdRef.current,
      after: afterRef.current,
      startedAt: startedAtRef.current,
    });
  }, []);

  const applyMessages = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(messagesRef.current) : updater;
    messagesRef.current = next;
    setMessages(next);
    return next;
  }, []);

  const finishTurnClock = useCallback(() => {
    const started = startedAtRef.current;
    startedAtRef.current = null;
    jobIdRef.current = null;
    afterRef.current = 0;
    if (started == null) return;
    applyMessages((prev) => stampTurnElapsed(prev, Date.now() - started));
  }, [applyMessages]);

  const handleEvent = useCallback(
    ({ event, data }) => {
      if (event === 'token' && data?.text) {
        applyMessages((prev) =>
          appendAssistantToken(prev, data.text).map((msg, i, arr) =>
            i === arr.length - 1 && msg.timestamp == null
              ? { ...msg, timestamp: Date.now() }
              : msg
          )
        );
        return;
      }
      if (event === 'tool') {
        applyMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            kind: 'tool',
            content: data?.name
              ? `tool ${data.name}${data.parameters?.path ? ` ${data.parameters.path}` : ''}`
              : 'tool',
            timestamp: Date.now(),
          },
        ]);
        return;
      }
      if (event === 'tool_result') {
        const result = data?.result ?? data;
        applyMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            kind: 'result',
            content: formatFleetChatResult(data?.name, result),
            timestamp: Date.now(),
          },
        ]);
        return;
      }
      if (event === 'confirmation_required') {
        const card = { ...data, savedAt: Date.now() };
        pendingRef.current = card;
        setPending(card);
        finishTurnClock();
        persistNow();
        return;
      }
      if (event === 'error') {
        setError(data?.message || 'Fleet Chat error');
        finishTurnClock();
        persistNow();
      }
    },
    [applyMessages, finishTurnClock, persistNow]
  );

  handleEventRef.current = handleEvent;
  persistNowRef.current = persistNow;
  finishTurnClockRef.current = finishTurnClock;

  const stopPoll = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    busyRef.current = false;
    setBusy(false);
  }, []);

  const wipeMemory = useCallback(() => {
    messagesRef.current = [];
    pendingRef.current = null;
    attachmentRef.current = null;
    inputRef.current = '';
    jobIdRef.current = null;
    afterRef.current = 0;
    startedAtRef.current = null;
    setMessages([]);
    setInput('');
    setPending(null);
    setAttachment(null);
    setAttachProgress(null);
    setError(null);
    setBusy(false);
    busyRef.current = false;
  }, []);

  const cancelAndWipe = useCallback(
    async (name) => {
      const id = jobIdRef.current;
      stopPoll();
      await cancelFleetChatJob({
        jobId: id,
        pollUrlFor: (jobId) => `/api/assistant/fleet-chat/jobs/${jobId}`,
        tokenFor: () => (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : ''),
      });
      wipeMemory();
      if (name) clearFleetChatSession(sessionStorage, name);
    },
    [stopPoll, wipeMemory]
  );

  useLayoutEffect(() => {
    const prev = usernameRef.current;
    usernameRef.current = username;
    hydratedRef.current = false;
    setReady(false);
    if (prev && prev !== username) {
      stopPoll();
    }
    if (!username) {
      wipeMemory();
      return undefined;
    }
    const snap = loadFleetChatSession(sessionStorage, username);
    const nextMessages = snap?.messages || [];
    messagesRef.current = nextMessages;
    pendingRef.current = snap?.pending || null;
    attachmentRef.current = snap?.attachment || null;
    inputRef.current = snap?.input || '';
    jobIdRef.current = snap?.jobId || null;
    afterRef.current = snap?.after || 0;
    startedAtRef.current = snap?.startedAt || null;
    setMessages(nextMessages);
    setInput(snap?.input || '');
    setPending(snap?.pending || null);
    setAttachment(snap?.attachment || null);
    setError(null);
    hydratedRef.current = true;
    setReady(true);
    if (!snap?.jobId) {
      setBusy(false);
      busyRef.current = false;
      return undefined;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    busyRef.current = true;
    setBusy(true);
    pollFleetChatJob({
      jobId: snap.jobId,
      after: snap.after || 0,
      pollUrlFor: (jobId) => `/api/assistant/fleet-chat/jobs/${jobId}`,
      tokenFor: () => (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : ''),
      onEvent: (ev) => handleEventRef.current?.(ev),
      onCursor: (after, done) => {
        afterRef.current = after;
        if (done) jobIdRef.current = null;
        persistNowRef.current?.();
      },
      signal: controller.signal,
      cancelOnAbort: false,
    })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setError(wrapFleetChatError(err).message);
        finishTurnClockRef.current?.();
      })
      .finally(() => {
        if (abortRef.current === controller) {
          busyRef.current = false;
          setBusy(false);
          persistNowRef.current?.();
        }
      });
    return () => {
      controller.abort();
      if (abortRef.current === controller) abortRef.current = null;
      busyRef.current = false;
    };
  }, [username, stopPoll, wipeMemory]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    persistNow();
  }, [messages, pending, input, attachment, persistNow]);

  useEffect(() => {
    const onLogout = () => {
      const name = usernameRef.current;
      cancelAndWipe(name);
    };
    window.addEventListener('user-logout', onLogout);
    return () => window.removeEventListener('user-logout', onLogout);
  }, [cancelAndWipe]);

  const runRequest = useCallback(
    async (body, userLine) => {
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '';
      if (!token) {
        setError('Not signed in.');
        return;
      }
      if (busyRef.current) return;
      if (userLine) {
        const startedAt = Date.now();
        startedAtRef.current = startedAt;
        applyMessages((prev) => [
          ...prev,
          { role: 'user', content: userLine, timestamp: startedAt, startedAt },
        ]);
      }
      busyRef.current = true;
      setBusy(true);
      setError(null);
      setPending(null);
      pendingRef.current = null;
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        await readFleetChatJob(
          body,
          handleEvent,
          (jobId) => {
            jobIdRef.current = jobId;
            afterRef.current = 0;
            persistNow();
          },
          (after, done) => {
            afterRef.current = after;
            if (done) jobIdRef.current = null;
            persistNow();
          },
          controller.signal
        );
        finishTurnClock();
      } catch (err) {
        if (err?.name !== 'AbortError') {
          setError(err?.message || wrapFleetChatError(err).message);
          finishTurnClock();
        }
      } finally {
        if (abortRef.current === controller) {
          busyRef.current = false;
          setBusy(false);
          persistNow();
        }
      }
    },
    [applyMessages, handleEvent, persistNow, finishTurnClock]
  );

  const sendMessage = useCallback(
    (text) => {
      const message = (text ?? input).trim();
      if (!message || !hydratedRef.current || busy || busyRef.current) return false;
      setInput('');
      inputRef.current = '';
      const staged = attachmentRef.current;
      runRequest(
        {
          message,
          history: transcriptForModel(messages),
          ...(staged?.stage_id ? { attachment: { stage_id: staged.stage_id } } : {}),
        },
        message
      );
      return true;
    },
    [input, busy, messages, runRequest]
  );

  const attachFile = useCallback(
    async (file) => {
      const problem = validateFleetChatAttachFile(file);
      if (problem) {
        setError(problem);
        return false;
      }
      setAttachProgress({ percent: 0, loaded: 0, total: file.size || 0 });
      try {
        const staged = await stageFleetChatFile({
          file,
          tokenFor: () => (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : ''),
          onProgress: setAttachProgress,
        });
        attachmentRef.current = staged;
        setAttachment(staged);
        setAttachProgress(null);
        persistNow();
        return true;
      } catch (err) {
        setAttachProgress(null);
        setError(err?.message || 'Upload failed.');
        return false;
      }
    },
    [persistNow]
  );

  const clearAttachment = useCallback(() => {
    attachmentRef.current = null;
    setAttachment(null);
    setAttachProgress(null);
    persistNow();
  }, [persistNow]);

  const approvePendingWithAck = useCallback(
    (highRiskAck) => {
      if (!pending?.pending_id || !pending?.tool || !hydratedRef.current || busy || busyRef.current) {
        return false;
      }
      if (pending.high_risk && !highRiskAck) {
        setError('Check the high-risk acknowledgement before approving.');
        return false;
      }
      const lastUser = [...messages].reverse().find((m) => m.role === 'user');
      if (pending.preview?.kind === 'firmware' || pending.preview?.kind === 'container') {
        clearAttachment();
      }
      runRequest(
        buildApproveBody({
          message: lastUser?.content || '',
          history: transcriptForModel(messages),
          pendingId: pending.pending_id,
          highRisk: pending.high_risk && highRiskAck,
        }),
        `Approve ${pending.method || ''} ${pending.path || ''} on ${pending.host || ''}`
      );
      return true;
    },
    [pending, busy, messages, runRequest, clearAttachment]
  );

  const setDraft = useCallback((next) => {
    setInput((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      inputRef.current = value;
      return value;
    });
  }, []);

  const denyPending = useCallback(() => {
    pendingRef.current = null;
    setPending(null);
    persistNow();
  }, [persistNow]);

  const clearTranscript = useCallback(() => {
    cancelAndWipe(usernameRef.current);
  }, [cancelAndWipe]);

  const value = useMemo(
    () => ({
      messages,
      input,
      setInput: setDraft,
      busy,
      ready,
      error,
      setError,
      pending,
      attachment,
      attachProgress,
      attachFile,
      clearAttachment,
      sendMessage,
      approvePendingWithAck,
      denyPending,
      clearTranscript,
    }),
    [
      messages,
      input,
      busy,
      ready,
      error,
      pending,
      attachment,
      attachProgress,
      attachFile,
      clearAttachment,
      sendMessage,
      approvePendingWithAck,
      denyPending,
      clearTranscript,
      setDraft,
    ]
  );

  return <FleetChatContext.Provider value={value}>{children}</FleetChatContext.Provider>;
}

export function useFleetChat() {
  const ctx = useContext(FleetChatContext);
  if (!ctx) {
    throw new Error('useFleetChat requires FleetChatProvider');
  }
  return ctx;
}
